import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, MapPinIcon, PlusIcon, SparklesIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';
import { WizardLayout } from '../../components/WizardLayout';
import { Chip } from '../../components/ui/Chip';
import { NumberStepper } from '../../components/ui/NumberStepper';
import { RangeSlider } from '../../components/ui/RangeSlider';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { Switch } from '../../components/ui/Switch';
import { Button } from '../../components/ui/Button';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { eventPhotoLibrary } from '../../data/images';
import { locations, type PlaceOption } from '../../data/locations';
import { eventTypes, genres, themes } from '../../data/options';
import { baht, bookingFee, formatDate } from '../../utils/format';
import { spring } from '../../utils/motion';
import type { ActType, EventType, LineupSlot } from '../../types/marketplace';
const steps = [{
  title: 'What’s the event?',
  subtitle: 'Give performers a feel for the night.'
}, {
  title: 'When & where',
  subtitle: 'Performers see distance and travel cost automatically.'
}, {
  title: 'Build your line-up',
  subtitle: 'Add a slot for each act you need — mix singers, duos and bands.'
}, {
  title: 'Budget & crowd',
  subtitle: 'Only pay the booking fee when a performer accepts.'
}, {
  title: 'Add photos',
  subtitle: 'The first photo is your cover. Events with photos get far more applicants.'
}, {
  title: 'Review & publish'
}];
const field = 'h-14 w-full rounded-sm border border-line bg-surface px-4 text-[17px] font-semibold placeholder:font-normal placeholder:text-ink-3 focus:border-ink focus:outline-none';
const toggle = <T,>(list: T[], item: T) => list.includes(item) ? list.filter(x => x !== item) : [...list, item];
let slotSeq = 0;
const newSlot = (kind: ActType = 'Solo singer'): LineupSlot => ({
  id: `new-${++slotSeq}`,
  kind,
  setLength: 60,
  budget: kind === 'Band' ? 30000 : 9000
});
export function CreateEvent() {
  const navigate = useNavigate();
  const {
    createEvent
  } = useMarketplace();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [name, setName] = useState('');
  const [type, setType] = useState<EventType>('Bar gig');
  const [theme, setTheme] = useState(themes[0]);
  const [eventGenres, setEventGenres] = useState<string[]>(['Soul']);
  const [date, setDate] = useState('2026-11-21');
  const [time, setTime] = useState('19:30');
  const [venue, setVenue] = useState('Halcyon Rooftop');
  const [query, setQuery] = useState('');
  const [place, setPlace] = useState<PlaceOption | null>(locations[0]);
  const [slots, setSlots] = useState<LineupSlot[]>([newSlot()]);
  const [capacity, setCapacity] = useState(150);
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [featured, setFeatured] = useState(false);
  const total = slots.reduce((s, x) => s + x.budget, 0);
  const canNext = [name.trim() && eventGenres.length, date && venue.trim() && place, slots.length > 0, true, photos.length > 0, true][step];
  const go = (d: number) => {
    setDir(d);
    setStep(s => s + d);
  };
  const updateSlot = (id: string, patch: Partial<LineupSlot>) => setSlots(list => list.map(s => s.id === id ? {
    ...s,
    ...patch
  } : s));
  const publish = () => {
    if (!place) return;
    createEvent({
      id: `e-${Date.now()}`,
      name: name.trim(),
      organiser: 'Halcyon Rooftop',
      ownedByMe: true,
      date,
      startTime: time,
      city: place.label,
      venue: venue.trim(),
      lat: place.lat,
      lng: place.lng,
      distanceKm: place.distanceKm,
      theme,
      type,
      genres: eventGenres,
      capacity,
      slots: slots.map((s, i) => ({
        ...s,
        id: `${Date.now()}-s${i}`
      })),
      photos,
      description: description.trim() || `${theme} ${type.toLowerCase()} at ${venue}.`,
      featured,
      applicantIds: [],
      status: 'live'
    });
    toast.success('Event published', {
      description: featured ? 'Featured at the top of performer feeds.' : 'Performers can now apply.'
    });
    navigate('/o/events');
  };
  const matchesPlaces = locations.filter(l => l.label.toLowerCase().includes(query.toLowerCase()));
  return <WizardLayout step={step} total={steps.length} direction={dir} title={steps[step].title} subtitle={steps[step].subtitle} onBack={() => step === 0 ? navigate('/o/events') : go(-1)} onNext={() => step === steps.length - 1 ? publish() : go(1)} nextLabel={step === steps.length - 1 ? featured ? 'Publish & feature · ฿490' : 'Publish event' : 'Continue'} canNext={Boolean(canNext)} data-id="element-924" data-mp="1jsrbncxx0db">
      {step === 0 && <div className="space-y-8" data-id="element-925" data-mp="kblm9nbkkvno">
          <label className="block" data-id="element-926" data-mp="24ze8yyc8tfte">
            <span className="text-[15px] font-bold" data-id="element-927" data-mp="24ycwcpfkwl9k">Event name</span>
            <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Rooftop Soul Sessions" className={`${field} mt-2`} data-id="element-928" data-mp="2001miepaaarc" />
          </label>
          <ChipGroup label="Type" options={eventTypes} isOn={t => t === type} onToggle={t => setType(t)} data-id="element-929" data-mp="29r61heeylvm6" />
          <ChipGroup label="Theme" options={themes} isOn={t => t === theme} onToggle={t => setTheme(t)} data-id="element-930" data-mp="1m0s7hictdmoy" />
          <ChipGroup label="Genres you want" options={genres} isOn={g => eventGenres.includes(g)} onToggle={g => setEventGenres(l => toggle(l, g))} data-id="element-931" data-mp="1h30hwuzn6cnx" />
        </div>}

      {step === 1 && <div className="space-y-6" data-id="element-932" data-mp="1r050yly6xs0i">
          <div className="grid grid-cols-2 gap-3" data-id="element-933" data-mp="1qz3gjqvabt64">
            <label className="block" data-id="element-934" data-mp="37dva474636h0">
              <span className="text-[15px] font-bold" data-id="element-935" data-mp="37ctlt0y54nhc">Date</span>
              <input type="date" value={date} min="2026-10-07" onChange={e => setDate(e.target.value)} className={`${field} mt-2`} data-id="element-936" data-mp="32gcxo6qxy949" />
            </label>
            <label className="block" data-id="element-937" data-mp="39l4bsog5iec2">
              <span className="text-[15px] font-bold" data-id="element-938" data-mp="3eh1tay35hqqf">Start time</span>
              <input type="time" value={time} onChange={e => setTime(e.target.value)} className={`${field} mt-2`} data-id="element-939" data-mp="3jgxxaxbuli5z" />
            </label>
          </div>
          <label className="block" data-id="element-940" data-mp="3oepqrvs55cca">
            <span className="text-[15px] font-bold" data-id="element-941" data-mp="3odob1e06k1o8">Venue name</span>
            <input value={venue} onChange={e => setVenue(e.target.value)} className={`${field} mt-2`} data-id="element-942" data-mp="2qttpkkldepqi" />
          </label>
          <div data-id="element-943" data-mp="2vp8weskynldf">
            <label className="block" data-id="element-944" data-mp="30qg8h3u4z6pw">
              <span className="text-[15px] font-bold" data-id="element-945" data-mp="30petioa0202w">Location</span>
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search area or town" className={`${field} mt-2`} data-id="element-946" data-mp="35gwn2r9p0ydu" />
            </label>
            <ul className="mt-2 divide-y divide-line" role="listbox" aria-label="Locations" data-id="element-947" data-mp="281isj7dedm0b">
              {matchesPlaces.map(l => {
            const on = place?.label === l.label;
            return <li key={l.label} data-id="element-948" data-mp="u3epgctjxzb5">
                    <button type="button" role="option" aria-selected={on} onClick={() => setPlace(l)} className="flex w-full items-center gap-3 rounded-sm px-2 py-3 text-left transition-colors duration-150 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-949" data-mp="q3w63lhdpwaf">
                      <MapPinIcon className="h-5 w-5 text-ink-3" data-id="element-950" data-mp="6evhjyeezojl" />
                      <span className="flex-1 font-semibold" data-id="element-951" data-mp="2dryz44zfeeo">{l.label}</span>
                      {on && <motion.span initial={{
                  scale: 0.6
                }} animate={{
                  scale: 1
                }} transition={spring.bouncy} data-id="element-952" data-mp="3tlxmij0wwfc7">
                          <CheckIcon className="h-5 w-5 text-brand-strong" data-id="element-953" data-mp="3ol9vhxconyhz" />
                        </motion.span>}
                    </button>
                  </li>;
          })}
            </ul>
          </div>
        </div>}

      {step === 2 && <div data-id="element-954" data-mp="1gcju08xyhv2x">
          <ul className="space-y-3" data-id="element-955" data-mp="1gcab15bbwo6q">
            <AnimatePresence initial={false} data-id="element-956" data-mp="12dkebqsf8qiw">
              {slots.map((s, i) => <motion.li key={s.id} layout initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            x: -24
          }} transition={spring.smooth} className="rounded-md bg-surface-2 p-4" data-id="element-957" data-mp="17cevypenk91f" data-mpi={i}>
                  <div className="flex items-center justify-between" data-id="element-958" data-mp="jo40y2vn13l2">
                    <p className="font-bold" data-id="element-959" data-mp="ole1adkkezno">Slot {i + 1}</p>
                    {slots.length > 1 && <button type="button" onClick={() => setSlots(l => l.filter(x => x.id !== s.id))} aria-label={`Remove slot ${i + 1}`} className="grid h-9 w-9 place-items-center rounded-full text-ink-3 transition-colors duration-150 hover:bg-bg hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-960" data-mp="9od8s7ujv0o5">
                        <Trash2Icon className="h-4 w-4" data-id="element-961" data-mp="4xrdriimgy7v" />
                      </button>}
                  </div>
                  <SegmentedControl<ActType> ariaLabel={`Act type for slot ${i + 1}`} layoutId={`kind-${s.id}`} className="mt-3 flex w-full bg-bg" size="sm" value={s.kind} onChange={kind => updateSlot(s.id, {
              kind
            })} options={[{
              value: 'Solo singer',
              label: 'Singer'
            }, {
              value: 'Duo',
              label: 'Duo'
            }, {
              value: 'Band',
              label: 'Band'
            }]} data-id="element-962" data-mp="e00kryeom9gi" />
                  <div className="mt-4 flex flex-wrap gap-2" data-id="element-963" data-mp="7i21lu8wdtj5">
                    {[30, 45, 60, 90, 120].map(len => <Chip key={len} selected={s.setLength === len} onClick={() => updateSlot(s.id, {
                setLength: len
              })} data-id="element-964" data-mp="2pqyyjmrvtmw">
                        {len} min
                      </Chip>)}
                  </div>
                  <div className="mt-4" data-id="element-965" data-mp="3pxciqc3mispb">
                    <NumberStepper label="Budget" value={s.budget} step={1000} min={1000} onChange={budget => updateSlot(s.id, {
                budget
              })} format={baht} data-id="element-966" data-mp="qgwfz24g8rys" />
                  </div>
                </motion.li>)}
            </AnimatePresence>
          </ul>
          <div className="mt-4 flex items-center justify-between" data-id="element-967" data-mp="hhsszn25vsf0">
            <Button variant="secondary" icon={<PlusIcon className="h-4 w-4" data-id="element-969" data-mp="ciy2s01v71kv" />} disabled={slots.length >= 6} onClick={() => setSlots(l => [...l, newSlot('Band')])} data-id="element-968" data-mp="cftuorla3lox">
              Add another act
            </Button>
            <p className="tabular text-[15px]" data-id="element-970" data-mp="2x5j5mtiv2ncc">
              Total <span className="font-bold" data-id="element-971" data-mp="1cpl2h8jkcxl2">{baht(total)}</span>
            </p>
          </div>
        </div>}

      {step === 3 && <div className="space-y-8" data-id="element-972" data-mp="1ho4qdehpwjg2">
          <RangeSlider label="Expected crowd" min={20} max={3000} step={10} value={capacity} onChange={setCapacity} format={v => `${v.toLocaleString('en-GB')} guests`} data-id="element-973" data-mp="1lpqa9jso5xzr" />
          <label className="block" data-id="element-974" data-mp="1qib06a8gfqy0">
            <span className="text-[15px] font-bold" data-id="element-975" data-mp="1vft0xdcbizux">Brief for performers</span>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder="Vibe, sound system, dress code, load-in…" className="mt-2 w-full resize-none rounded-sm border border-line bg-surface px-4 py-3 text-[16px] placeholder:text-ink-3 focus:border-ink focus:outline-none" data-id="element-976" data-mp="20ee1jdvhfljw" />
          </label>
          <dl className="space-y-2 rounded-md bg-surface-2 p-5 text-[15px]" data-id="element-977" data-mp="f0rp5vomujow">
            <div className="flex justify-between" data-id="element-978" data-mp="f2uqhbv8jz8x">
              <dt className="text-ink-2" data-id="element-979" data-mp="1id0agcehipk7">Performer budgets ({slots.length} acts)</dt>
              <dd className="tabular font-bold" data-id="element-980" data-mp="1ddnn7qjpbgh3">{baht(total)}</dd>
            </div>
            <div className="flex justify-between" data-id="element-981" data-mp="1rcdfmsvksrz4">
              <dt className="text-ink-2" data-id="element-982" data-mp="1rdye2tl0gv29">Booking fee (6%, on confirmed only)</dt>
              <dd className="tabular font-bold" data-id="element-983" data-mp="1na8ruart6pke">~{baht(bookingFee(total))}</dd>
            </div>
            <div className="flex justify-between border-t border-line pt-2" data-id="element-984" data-mp="219i7n9c3rhnu">
              <dt className="font-bold" data-id="element-985" data-mp="1wc86wxj9jjkp">Estimated total</dt>
              <dd className="tabular text-lg font-bold" data-id="element-986" data-mp="1w9vuezw9y1f1">{baht(total + bookingFee(total))}</dd>
            </div>
          </dl>
        </div>}

      {step === 4 && <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-987" data-mp="2621lm7epcet6">
          {eventPhotoLibrary.map(p => {
        const idx = photos.indexOf(p);
        const on = idx >= 0;
        return <motion.button key={p} type="button" whileTap={{
          scale: 0.96
        }} transition={spring.snappy} onClick={() => setPhotos(l => toggle(l, p))} aria-pressed={on} aria-label={on ? `Remove photo ${idx + 1}` : 'Add photo'} className={twMerge('relative aspect-square overflow-hidden rounded-md ring-offset-2 ring-offset-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand', on && 'ring-[3px] ring-brand')} data-id="element-988" data-mp="2f412mzppviw4">
                <img src={p} alt="" className="h-full w-full object-cover" data-id="element-989" data-mp="25zca6wpqc2i2" />
                <AnimatePresence data-id="element-990" data-mp="25x999hn1goyk">
                  {on && <motion.span initial={{
              scale: 0.5,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} exit={{
              scale: 0.5,
              opacity: 0
            }} transition={spring.bouncy} className="tabular absolute right-2 top-2 grid h-7 min-w-7 place-items-center rounded-full bg-brand px-2 text-sm font-bold text-on-brand" data-id="element-991" data-mp="2azgmsbniod30">
                      {idx === 0 ? 'Cover' : idx + 1}
                    </motion.span>}
                </AnimatePresence>
              </motion.button>;
      })}
        </div>}

      {step === 5 && <div data-id="element-992" data-mp="1b73007a82d1h">
          <div className="overflow-hidden rounded-lg bg-surface-2" data-id="element-993" data-mp="1b7l23kjkflhb">
            {photos[0] && <img src={photos[0]} alt="" className="aspect-[16/9] w-full object-cover" data-id="element-994" data-mp="166xd14ewdepq" />}
            <div className="p-5" data-id="element-995" data-mp="1183wf34bd7lc">
              <p className="text-2xl font-bold" data-id="element-996" data-mp="11959te8sy668">{name}</p>
              <p className="mt-1 text-[15px] text-ink-2" data-id="element-997" data-mp="x71wsl6hitiu">
                {formatDate(date, 'EEEE d MMMM')} · {time} · {venue}, {place?.label}
              </p>
              <ul className="mt-4 divide-y divide-line border-t border-line" data-id="element-998" data-mp="2vllpf5nhmwnr">
                {slots.map((s, i) => <li key={s.id} className="flex justify-between py-2.5 text-[15px]" data-id="element-999" data-mp="2riyuuwzfmxmv" data-mpi={i}>
                    <span data-id="element-1000" data-mp="daxy2fail3p6">
                      Slot {i + 1} · {s.kind} · {s.setLength} min
                    </span>
                    <span className="tabular font-bold" data-id="element-1001" data-mp="n2tvrp8oursv">{baht(s.budget)}</span>
                  </li>)}
              </ul>
              <p className="mt-3 text-sm text-ink-3" data-id="element-1002" data-mp="r3ds4qlftrdy">
                {theme} · {type} · {capacity.toLocaleString('en-GB')} guests · {eventGenres.join(', ')}
              </p>
            </div>
          </div>
          <div className="mt-6 rounded-md border border-line p-5" data-id="element-1003" data-mp="3vjgb8ivqlohi">
            <div className="mb-3 flex items-center gap-2 text-brand-strong" data-id="element-1004" data-mp="3f4eibydo9xw">
              <SparklesIcon className="h-5 w-5" data-id="element-1005" data-mp="3h78t86kcp8f" />
              <span className="font-bold" data-id="element-1006" data-mp="8f0jadewg5ei">Fill it faster</span>
            </div>
            <Switch label="Feature this event · ฿490" description="Top of every matching performer’s feed until your line-up is full." checked={featured} onChange={setFeatured} data-id="element-1007" data-mp="1eusz4wo5swvj" />
          </div>
        </div>}
    </WizardLayout>;
}
interface ChipGroupProps<T extends string> {
  label: string;
  options: readonly T[];
  isOn: (v: T) => boolean;
  onToggle: (v: T) => void;
}
function ChipGroup<T extends string>({
  label,
  options,
  isOn,
  onToggle
}: ChipGroupProps<T>) {
  return <div data-id="element-1008" data-mp="3kt9975pst4il">
      <p className="mb-3 text-[15px] font-bold" data-id="element-1009" data-mp="2x5r8ulsgwy40">{label}</p>
      <div className="flex flex-wrap gap-2" data-id="element-1010" data-mp="2x6ary5tutsjm">
        {options.map(o => <Chip key={o} selected={isOn(o)} onClick={() => onToggle(o)} data-id="element-1011" data-mp="2s7pqy46s34u4">
            {o}
          </Chip>)}
      </div>
    </div>;
}